import { Box, Dialog, Icon, P, toast } from '@a-type/ui';
import {
	CONFIG,
	LinkButton,
	LoginButton,
	TextLink,
	getIsPWAInstalled,
	getOS,
	useAppInfo,
	useHasServerAccess,
} from '@biscuits/client';
import { ExportDataButton } from '@biscuits/client/apps';

export interface AppMovedProps {}

export function AppMoved({}: AppMovedProps) {
	const canSync = useHasServerAccess();
	const app = useAppInfo();

	if (
		window.location.origin === app.url ||
		window.location.origin === app.devOriginOverride ||
		// special case for localnet...
		window.location.origin.startsWith('http://192.')
	) {
		return null;
	}

	const goTo = new URL(app.url);
	if (!canSync) {
		goTo.search = '?transfer=true';
	}

	return (
		<Dialog open>
			<Dialog.Content>
				<Dialog.Title>Gnocchi has moved</Dialog.Title>
				<P>
					Sorry, I know this is annoying, but Gnocchi has moved to a new app to
					join{' '}
					<TextLink newTab href={CONFIG.HOME_ORIGIN}>
						Biscuits
					</TextLink>
					. You'll have to go there to use the app now.
				</P>
				{!canSync ? <LoggedOut /> : <LoggedIn />}
				<InstallNote />
				<Dialog.Actions>
					<LinkButton href={`${CONFIG.HOME_ORIGIN}/contact`} newTab>
						Contact Support
					</LinkButton>
					<LinkButton emphasis="primary" href={goTo.toString()} newTab>
						Open Gnocchi
					</LinkButton>
				</Dialog.Actions>
			</Dialog.Content>
		</Dialog>
	);
}

function LoggedOut() {
	if (getOS() === 'iOS' && getIsPWAInstalled()) {
		return (
			<Box col>
				<P>
					You're not currently logged in to sync. If you have a subscription,
					log in now so your data syncs to the new app.
				</P>
				<LoginButton />
				<P>
					If you don't have a sync subscription, you can export your data now
					and import it into the new app (click the <Icon name="gear" /> in the
					top right after opening the new app).
				</P>
				<ExportDataButton onError={(e) => toast.error(e.message)} />
			</Box>
		);
	} else {
		return (
			<Box col>
				<P>
					Your data should transfer automatically when you open the new app. If
					that doesn't seem to work, come back to this app and export your data
					as a file. You can then use the <Icon name="gear" /> settings menu in
					the new app to import the file.
				</P>
				<ExportDataButton onError={(e) => toast.error(e.message)} />
			</Box>
		);
	}
}

function LoggedIn() {
	return (
		<P>
			Since you're logged into sync, all your data will be available in the new
			app automatically. Just click the button below to open the new app.
		</P>
	);
}

function InstallNote() {
	const os = getOS();
	if (os === 'iOS') {
		return (
			<div>
				Open the new app in Safari to add it to your home screen again. If
				you've already installed the new app, you can uninstall this one.
			</div>
		);
	} else if (os === 'Android') {
		return (
			<div>
				Open the new app in Chrome and add it to your home screen again. If
				you've already installed the new app, you can uninstall this one.
			</div>
		);
	}
	return null;
}
